'use client';

import { useChainConfig } from '@/hooks/use-chain-config';
import { useInvestorQuery } from '@/hooks/use-investor-query';
import { useWalletSession } from '@/hooks/use-wallet-session';
import { fetchInvestorHoldings } from '../api/get-investor-holdings';
import { portfolioKeys } from '@/lib/investor-keys';
import type {
  InvestorHoldingsQuery,
  InvestorHoldingsResponse
} from '../types/api';

const defaultHoldingsQuery = {
  page: 1,
  limit: 100,
  sort: 'value'
} as const satisfies InvestorHoldingsQuery;

export function useInvestorHoldings(
  params: InvestorHoldingsQuery = defaultHoldingsQuery
) {
  const { identityAddress } = useWalletSession();
  const { effectiveChainId } = useChainConfig();

  const query = useInvestorQuery<InvestorHoldingsResponse>({
    key: ({ address, chainId }) =>
      portfolioKeys.holdings(address, chainId, params),
    fetch: ({ address, chainId }, signal) =>
      fetchInvestorHoldings(address, chainId, params, signal),
    // Turning a page must not blank the cards that read this query, but
    // rows from a previous wallet must never appear under this one — the
    // factory tests the scope.
    keepPreviousWithinScope: true
  });

  return {
    ...query,
    // Callers reset their pagination when this changes.
    scopeKey: `${identityAddress?.toLowerCase() ?? 'disconnected'}:${effectiveChainId}`
  };
}
